import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import {
  formatTextContent,
  getConfigurationItems,
  getWarmConfigurationItems,
} from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px);
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
`;

const ProgressBarContainer = styled.div`
  width: 50vw;
  max-width: 280px;
  margin: 0 auto 32px auto;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => (props.progress / props.total) * 100}%;
  background: ${theme.colors.doryGreen};
  border-radius: 2px;
  transition: width ${theme.animation.durations.normal};
`;

const Title = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: 1.1;
  text-wrap: balance;
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0 0 ${theme.spacing.lg} 0;
  text-wrap: balance;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-bottom: ${theme.spacing.lg};
  text-align: left;
`;

const OptionCard = styled.button`
  width: 100%;
  text-align: left;
  border-radius: ${theme.borderRadius.lg};
  border: 2px solid ${(props) => (props.$selected ? theme.colors.doryGreen : 'rgba(64, 92, 115, 0.18)')};
  background: ${(props) => (props.$selected ? 'rgba(67, 95, 96, 0.12)' : '#fff')};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  display: flex;
  gap: ${theme.spacing.md};
  align-items: flex-start;
  transition: all ${theme.animation.durations.fast};

  &:hover {
    border-color: ${theme.colors.doryBlue};
    transform: translateY(-1px);
  }
`;

const Checkbox = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.$selected ? theme.colors.doryGreen : theme.colors.doryLighterBlue)};
  background: ${(props) => (props.$selected ? theme.colors.doryGreen : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  font-weight: ${theme.typography.weights.bold};
`;

const OptionLabel = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.relaxed};
  text-wrap: balance;
`;

const SelectionHint = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.doryBlue};
  margin: 0 0 ${theme.spacing.md} 0;
  text-wrap: balance;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-top: ${theme.spacing.md};
`;

const PrimaryButton = styled.button`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.semiBold};
  min-height: 52px;
  width: 100%;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  color: #fff;
  cursor: pointer;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: #8b4a4a;
  margin: 0 0 ${theme.spacing.md} 0;
`;

const LoadingText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.doryBlue};
  font-style: italic;
  margin: ${theme.spacing.md} 0 ${theme.spacing.md} 0;
  width: 100%;
  text-align: left;
`;

const WhichAreYouScreen = ({ data, onResponse, userProgress = {} }) => {
  const content = data?.content || {};
  const htmlIntro = content.text
    ? formatTextContent(content.text, userProgress)
    : null;

  const [roles, setRoles] = useState(() => getWarmConfigurationItems('types'));
  const [isLoadingItems, setIsLoadingItems] = useState(
    () => getWarmConfigurationItems('types').length === 0,
  );
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const title =
    'The Dory Protocol is for high-pressure high-achievers only. Which type are you?';

  useEffect(() => {
    let cancelled = false;
    const hadWarmItems = getWarmConfigurationItems('types').length > 0;

    const loadItems = async () => {
      // Already have snapshot/cache — refresh in background without blanking UI
      if (!hadWarmItems) {
        setIsLoadingItems(true);
      }
      setError('');
      try {
        const items = await getConfigurationItems('types');
        if (cancelled) return;
        if (items.length > 0) {
          setRoles(items);
        }
      } catch (err) {
        console.error('Failed to load types configuration:', err);
        if (!cancelled && !hadWarmItems) {
          setError('Failed to load options. Please refresh and try again.');
        }
      } finally {
        if (!cancelled) setIsLoadingItems(false);
      }
    };

    loadItems();
    return () => {
      cancelled = true;
    };
  }, []);

  const rolesFromIds = (ids) =>
    ids
      .map((id) => roles.find((r) => r.id === id))
      .filter(Boolean)
      .map((r) => ({ id: r.id, label: r.label }));

  const toggleRole = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const finishAndSubmit = async () => {
    if (isSubmitting || selectedIds.length === 0) return;
    setIsSubmitting(true);
    setError('');

    const responseData = {
      checkpointKey: data.key,
      type: 'which_are_you',
      roles: rolesFromIds(selectedIds),
      timestamp: Date.now(),
    };

    try {
      await onResponse?.(responseData);
    } catch (err) {
      console.error('Failed to save which-are-you response:', err);
      setError('Failed to save your selection. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Content>
        <ProgressBarContainer>
          <ProgressFill progress={1} total={1} />
        </ProgressBarContainer>

        {isLoadingItems ? (
          <LoadingText>Loading options…</LoadingText>
        ) : (
          <>
            {htmlIntro && (
              <Description dangerouslySetInnerHTML={{ __html: htmlIntro }} />
            )}

            <Title>{title}</Title>

            <SelectionHint>Select all that apply</SelectionHint>

            <OptionList>
              {roles.map((item) => {
                const selected = selectedIds.includes(item.id);
                return (
                  <OptionCard
                    key={item.id}
                    type="button"
                    $selected={selected}
                    onClick={() => toggleRole(item.id)}
                    aria-pressed={selected}
                  >
                    <Checkbox $selected={selected}>
                      {selected ? '✓' : ''}
                    </Checkbox>
                    <OptionLabel>{item.label}</OptionLabel>
                  </OptionCard>
                );
              })}
            </OptionList>

            {error && <ErrorText>{error}</ErrorText>}

            <ButtonGroup>
              <PrimaryButton
                onClick={finishAndSubmit}
                disabled={
                  isSubmitting ||
                  selectedIds.length === 0 ||
                  roles.length === 0
                }
              >
                {isSubmitting ? 'Saving…' : content.ctaText || 'Continue'}
              </PrimaryButton>
            </ButtonGroup>
          </>
        )}
      </Content>
    </Container>
  );
};

export default WhichAreYouScreen;
